import { Injectable, inject } from '@angular/core';
import { Observable, from, firstValueFrom } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { OfflineService } from './offline.service';
import { PosOfflineDbService } from './pos-offline-db.service';
import { DiscountsApiService } from '../../discounts/discounts-api.service';
import { CustomersApiService } from '../../customers/customers-api.service';
import { StoresApiService } from '../../../core/stores/stores-api.service';
import { FindStorePosProductResponseDto } from '../../../core/stores/dtos/partials/find-store-pos-product.response.dto';
import { FindStorePosCategoryResponseDto } from '../../../core/stores/dtos/partials/find-store-pos-category.response.dto';
import { CashDrawersApiService } from '../../cash-drawers/cash-drawers-api.service';
import { ApplicableDiscount } from '../../../model/discount-applicable';
import { RegisterSelectorService } from '../register-selector/register-selector.service';
import { FindCustomerNamesQueryDto } from '../../customers/dtos/find-customer-names.query.dto';
import { FindCustomerNamesResponseDto } from '../../customers/dtos/find-customer-names.response.dto';
import { FindStorePosSettingsResponseDto } from '../../../core/stores/dtos/find-store-pos-settings.response.dto';
import { CurrentDeviceService } from '../current-device/current-device.service';
import { FindLatestCashRegisterSessionResponseDto } from '../../cash-drawers/cash-register-session/dtos/find-latest-cash-register-session.response.dto';
import { CashRegisterSessionsApiService } from '../../cash-drawers/cash-register-session/cash-register-session-api.service';

@Injectable({
  providedIn: 'root',
})
export class PosOfflineWrapperService {
  #offlineService = inject(OfflineService);
  #offlineDbService = inject(PosOfflineDbService);
  #discountsApiService = inject(DiscountsApiService);
  #customersApiService = inject(CustomersApiService);
  #cashDrawersApiService = inject(CashDrawersApiService);
  #cashRegisterSessionApiService = inject(CashRegisterSessionsApiService);
  #currentDevice = inject(CurrentDeviceService);
  #registerSelectorService = inject(RegisterSelectorService);
  #storesService = inject(StoresApiService);

  /**
   * Get applicable discounts with offline fallback
   */
  getApplicableDiscounts(
    storeId: number,
    amount: number,
    customerId?: number,
  ): Observable<ApplicableDiscount[]> {
    if (this.#offlineService.isOffline()) {
      return from(
        this.#offlineDbService.getDiscounts(storeId, amount, customerId),
      );
    }

    return this.#discountsApiService
      .getApplicableDiscounts(storeId, amount, customerId)
      .pipe(
        tap(async (discounts) => {
          const minAmount = Math.max(0, amount * 0.5);
          const maxAmount = amount * 1.5;
          await this.#offlineDbService.saveDiscounts(
            storeId,
            discounts,
            minAmount,
            maxAmount,
            customerId,
          );
        }),
        catchError((error) => {
          console.error('[POS Offline] Failed to fetch discounts:', error);
          return from(
            this.#offlineDbService.getDiscounts(storeId, amount, customerId),
          );
        }),
      );
  }

  /**
   * Search customers with offline fallback
   */
  async searchCustomers(
    query: FindCustomerNamesQueryDto,
  ): Promise<FindCustomerNamesResponseDto> {
    const storeId = this.#currentDevice.storeId();
    const page = query.page || 1;
    const size = query.size || 10;

    if (!storeId) {
      return {
        data: [],
        meta: { total: 0, page, size },
      };
    }

    if (this.#offlineService.isOffline()) {
      const searchValue = query.searchValue || '';
      const customers = searchValue
        ? await this.#offlineDbService.searchCustomers(storeId, searchValue)
        : await this.#offlineDbService.getAllCustomers(storeId);

      const start = (page - 1) * size;
      const end = start + size;

      return {
        data: customers.slice(start, end),
        meta: {
          total: customers.length,
          page,
          size,
        },
      };
    }

    // Online: fetch from API and cache the result
    try {
      const result = await this.#customersApiService.findCustomerNames(query);
      await this.#offlineDbService.saveCustomers(storeId, result.data);
      return result;
    } catch (error) {
      console.error('[POS Offline] Failed to fetch customers:', error);
      const searchTerm = query.searchValue || '';
      const customers = searchTerm
        ? await this.#offlineDbService.searchCustomers(storeId, searchTerm)
        : await this.#offlineDbService.getAllCustomers(storeId);

      const start = (page - 1) * size;
      const end = start + size;

      return {
        data: customers.slice(start, end),
        meta: {
          total: customers.length,
          page,
          size,
        },
      };
    }
  }

  /**
   * Get latest cash register session with offline fallback
   */
  async findLatestCashRegisterSession(
    registerId: number,
  ): Promise<FindLatestCashRegisterSessionResponseDto> {
    try {
      const session =
        await this.#cashRegisterSessionApiService.findLatestCashRegisterSession(
          registerId,
        );

      if (session?.id) {
        const latestSession = session;
        const sessionStoreId = latestSession.register?.store?.id;
        const storeId = sessionStoreId || this.#currentDevice.storeId();

        if (storeId) {
          await this.#offlineDbService.saveRegisterSession(
            registerId,
            storeId,
            latestSession,
          );
        }

        this.#updateRegisterMetaFromSession(latestSession);
      }

      return session;
    } catch {
      return this.getOfflineRegisterSession(registerId);
    }
  }
  /**
   * Get register session from offline cache with proper register/store names
   */
  #updateRegisterMetaFromSession(
    session: FindLatestCashRegisterSessionResponseDto,
  ): void {
    if (session.register) {
      this.#registerSelectorService.updateRegisterMeta(
        session.register.id,
        session.register.machineIdentificationNumber ?? undefined,
        session.register.serialNumber ?? undefined,
      );
    }
  }

  private async getOfflineRegisterSession(
    registerId: number,
  ): Promise<FindLatestCashRegisterSessionResponseDto> {
    const cachedSession =
      await this.#offlineDbService.getRegisterSession(registerId);

    if (cachedSession) {
      this.#updateRegisterMetaFromSession(cachedSession);
      return cachedSession;
    }

    const cachedRegisters = await this.#offlineDbService.getRegisterList();
    const registerInfo = cachedRegisters?.find((r) => r.id === registerId);

    return {
      register: {
        id: registerInfo.id,
        name: registerInfo.name,
        machineIdentificationNumber: registerInfo.machineIdentificationNumber,
        serialNumber: registerInfo.serialNumber,
        store: {
          id: registerInfo.store.id,
          name: registerInfo.store.name,
          permitToUseNumber: registerInfo.store.permitToUseNumber,
          authorityToGenerateNumber:
            registerInfo.store.authorityToGenerateNumber,
        },
      },
      numOfClosuresToday: 0,
    };
  }

  /**
   * Preload all customers for a store (call this when online to prepare for offline)
   */
  async preloadCustomers(storeId: number): Promise<void> {
    if (this.#offlineService.isOffline()) {
      return;
    }

    try {
      const result = await this.#customersApiService.findCustomerNames({
        page: 1,
        size: 10000,
      });

      await this.#offlineDbService.saveCustomers(storeId, result.data);
    } catch (error) {
      console.error('[POS Offline] Failed to preload customers:', error);
    }
  }

  /**
   * Preload common discount ranges for a store
   */
  async preloadDiscounts(storeId: number): Promise<void> {
    if (this.#offlineService.isOffline()) {
      return;
    }

    try {
      const amounts = [100, 500, 1000, 2500, 5000, 10000];
      for (const amount of amounts) {
        const discounts = await firstValueFrom(
          this.#discountsApiService.getApplicableDiscounts(storeId, amount),
        );
        if (discounts) {
          const minAmount = Math.max(0, amount * 0.5);
          const maxAmount = amount * 1.5;
          await this.#offlineDbService.saveDiscounts(
            storeId,
            discounts,
            minAmount,
            maxAmount,
          );
        }
      }
    } catch (error) {
      console.error('[POS Offline] Failed to preload discounts:', error);
    }
  }

  /**
   * Preload selected store's data needed for offline invoice generation
   */
  async preloadSelectedStoreInvoiceMetadata(storeId: number): Promise<void> {
    if (this.#offlineService.isOffline()) {
      return;
    }

    try {
      const metadata =
        await this.#storesService.findStoreInvoiceMetadata(storeId);
      await this.#offlineDbService.saveSelectedStoreInvoiceMetadata(metadata);
    } catch (error) {
      console.error(
        `[POS Offline] Failed to preload selected store's invoice metadata:`,
        error,
      );
    }
  }

  /**
   * Preload register invoice series for offline invoice number generation
   */
  async preloadRegisterInvoiceSeries(registerId: number): Promise<void> {
    if (this.#offlineService.isOffline()) {
      return;
    }

    try {
      const series =
        await this.#cashDrawersApiService.findRegisterInvoiceSeries(registerId);
      await this.#offlineDbService.saveInvoiceSeries(registerId, series);
    } catch (error) {
      console.error(
        '[POS Offline] Failed to preload register invoice series:',
        error,
      );
    }
  }

  /**
   * Save product stocks to cache
   */
  async cacheProductStocks(
    storeId: number,
    stocks: { productId: number; stock: number | null }[],
  ): Promise<void> {
    await this.#offlineDbService.saveProductStocks(storeId, stocks);
  }

  /**
   * Get cached product stock
   */
  async getCachedProductStock(productId: number): Promise<number | null> {
    return await this.#offlineDbService.getProductStock(productId);
  }

  /**
   * Get all cached product stocks for a store
   */

  /**
   * Get POS data (products and categories) with offline fallback
   */
  async getStorePosData(storeId: number): Promise<{
    settings: FindStorePosSettingsResponseDto;
    products: FindStorePosProductResponseDto[];
    categories: FindStorePosCategoryResponseDto[];
  }> {
    try {
      const { settings, products, categories } =
        await this.#storesService.findStorePos(storeId, {
          page: 1,
          size: 1000000,
        });
      await this.#offlineDbService.saveProducts(storeId, products.data);
      await this.#offlineDbService.saveCategories(storeId, categories);
      await this.#offlineDbService.saveMetadata(
        `last_update_${storeId}`,
        Date.now(),
      );
      return { settings, products: products.data, categories };
    } catch {
      const settings = { maxVisibleCategories: 1 }; // dummy value
      const products = await this.#offlineDbService.getProducts(storeId);
      const categories = await this.#offlineDbService.getCategories(storeId);

      return { settings, products, categories };
    }
  }
}
